import { useEffect, useState } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";
import { useProjects } from "@/state/project";
import { guardCloseout } from "@/lib/perms";
import { onCloseoutSubmitted } from "@/lib/events";
import { getMyUserId } from "@/lib/auth";
import { listMembers, type ProjectMember } from "@/lib/projects";
import {
  listNcrAssignments,
  assignNcr,
  unassignNcr,
  punchKey,
  type NcrAssignment,
} from "@/lib/ncrAssignments";

// Punch list — faithful React port of vPunch(). Reads failed items across all
// scopes from the engine (punchByCat/punchCounts); resolve/jump/export wired.
function ScopeBadge({ scope }: { scope: string }) {
  const C = eng().C;
  const c = scope === "Pile" ? C.steel : scope === "Module" ? C.hiviz : C.dim2;
  return (
    <span className="scopechip" style={{ background: `${c}22`, color: c, border: `1px solid ${c}66` }}>
      {scope}
    </span>
  );
}

export default function PunchView() {
  const version = useGrid((s) => s.version);
  const bump = useGrid((s) => s.bump);
  const setView = useGrid((s) => s.setView);
  void version;

  const e = eng();
  const C = e.C;
  const D = e.D;
  const currentId = useProjects((s) => s.currentId);

  // NCR → contractor assignment (Task 3). Managers assign; contractors see only
  // items assigned to them; everyone else sees the assignee as a read-only chip.
  const isManager = e.currentRole === "qcmanager";
  const isContractor = e.currentRole === "contractor";
  const [assignments, setAssignments] = useState<Record<string, NcrAssignment>>({});
  const [contractors, setContractors] = useState<ProjectMember[]>([]);
  const [myUid, setMyUid] = useState<string | null>(null);

  const loadAssignments = async () => {
    const rows = await listNcrAssignments();
    setAssignments(Object.fromEntries(rows.map((r) => [r.punch_key, r])));
  };
  useEffect(() => {
    void loadAssignments();
    void getMyUserId().then(setMyUid);
    if (isManager) void listMembers(currentId).then((m) => setContractors(m.filter((x) => x.role === "contractor")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, isManager]);

  const assignTo = async (p: any, contractorId: string) => {
    try {
      if (contractorId) await assignNcr(punchKey(p), contractorId);
      else await unassignNcr(punchKey(p));
      await loadAssignments();
    } catch (err) {
      e.toast(err instanceof Error ? err.message : "Could not assign", "warn");
    }
  };

  // Issue #8: group by location (Field → Table), not inspection category, so a
  // crew sees every open item for a given table together.
  const byLocRaw = e.punchByLocation();
  // A contractor only sees punch items assigned to them.
  const byLoc: Record<string, any[]> = isContractor
    ? Object.fromEntries(
        Object.entries(byLocRaw)
          .map(([loc, items]) => [loc, (items as any[]).filter((p) => assignments[punchKey(p)]?.contractor_id === myUid)])
          .filter(([, items]) => (items as any[]).length > 0),
      )
    : (byLocRaw as Record<string, any[]>);
  const locs = Object.keys(byLoc);
  const pc = e.punchCounts();

  const toggle = (p: any) => {
    if (!guardCloseout()) return;
    const resolving = !p.resolved;
    e.Store.setResolved(p.key, +p.idx, resolving);
    e.saveToStorage();
    bump();
    if (resolving && p.ncr) onCloseoutSubmitted(p.ncr);
  };

  const jump = (p: any) => {
    e.state.qcItem = +p.idx;
    if (p.workScope === "station") {
      e.state.qcWorkScope = "station";
      e.state.qcStation = p.fid;
      e.state.qcInverter = p.t - 1;
    } else if (p.workScope === "site") {
      e.state.qcWorkScope = "site";
    } else {
      e.state.qcWorkScope = "table";
      e.state.qcField = p.fid;
      e.state.qcTable = p.t;
      e.state.qcModule = null;
      e.state.qcPile = null;
      e.state.showModules = false;
      e.state.showPiles = false;
      const f = D.fields.find((x: any) => x.id === p.fid) || D.fields[0];
      const nRows = e.rowsForField(f);
      for (let row = 1; row <= nRows; row++) {
        if (e.tablesInRow(f, row).includes(p.t)) {
          e.state.qcRow = row;
          break;
        }
      }
      if (p.scopeKey && p.scopeKey[0] === "M") {
        e.state.showModules = true;
        e.state.qcModule = +p.scopeKey.slice(1);
      } else if (p.scopeKey && p.scopeKey[0] === "P") {
        e.state.showPiles = true;
        e.state.qcPile = +p.scopeKey.slice(1);
      }
    }
    e.state.mobileStep = "item";
    setView("qc");
  };

  return (
    <div className="punchwrap">
      <div className="vhead">
        <h1 className="serif">Punch list</h1>
        <div className="vs">
          {D.project.name} · {D.project.location} · all rejected items requiring close-out
        </div>
        <div className="vtag">ITP-BAL · Rev B</div>
      </div>
      <div className="punchbar">
        <div className="punchstats">
          <div className="ps">
            <b style={{ color: C.rust }}>{pc.open}</b>
            <span>Open</span>
          </div>
          <div className="ps">
            <b style={{ color: C.grass }}>{pc.resolved}</b>
            <span>Resolved</span>
          </div>
          <div className="ps">
            <b>{pc.total}</b>
            <span>Total</span>
          </div>
          <div className="ps">
            <b>{locs.length}</b>
            <span>Locations</span>
          </div>
        </div>
        <div className="punchexport">
          <button className="export-btn pdf" onClick={() => e.exportPunchPDF()}>
            ⬇ Export PDF
          </button>
          <button className="export-btn xls" onClick={() => e.exportPunchExcel()}>
            ⬇ Export Excel
          </button>
        </div>
      </div>
      <div className="punchlist">
        {locs.length ? (
          locs.map((loc) => {
            const items = byLoc[loc];
            const open = items.filter((p: any) => !p.resolved).length;
            return (
              <div className="punchcat" key={loc}>
                <div className="punchcathead">
                  <span className="pch-name">{loc}</span>
                  <span className="pch-count">
                    {open} open{items.length - open ? ` · ${items.length - open} resolved` : ""}
                  </span>
                </div>
                {items.map((p: any) => (
                  <div className={`punchrow ${p.resolved ? "done" : ""}`} key={`${p.key}|${p.idx}`}>
                    <button
                      className={`punchchk ${p.resolved ? "on" : ""}`}
                      title={p.resolved ? "Reopen" : "Mark resolved"}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        toggle(p);
                      }}
                    >
                      {p.resolved ? "✓" : ""}
                    </button>
                    <div className="punchmain" onClick={() => jump(p)} style={{ cursor: "pointer" }}>
                      <div className="punchtop">
                        <span className="punchitem">{p.item}</span>
                        <ScopeBadge scope={p.scope} />
                        {p.ncr ? <span className="punchncr">{p.ncr}</span> : null}
                      </div>
                      <div className="punchmeta">
                        <span className="mono">{p.itp}</span> · <span className="mono">{p.loc}</span>
                        {p.by ? ` · ${p.by}` : ""}
                      </div>
                      {p.comment ? <div className="punchnote">“{p.comment}”</div> : null}
                    </div>
                    {isManager ? (
                      <select
                        value={assignments[punchKey(p)]?.contractor_id || ""}
                        onClick={(ev) => ev.stopPropagation()}
                        onChange={(ev) => assignTo(p, ev.target.value)}
                        title="Assign to a contractor"
                        style={{
                          maxWidth: 150, padding: "4px 6px", fontSize: 12, borderRadius: 6,
                          background: "var(--panel2,#141d27)", color: "var(--ink)", border: "1px solid var(--line)",
                        }}
                      >
                        <option value="">{contractors.length ? "Unassigned" : "No contractors"}</option>
                        {contractors.map((c) => (
                          <option key={c.user_id} value={c.user_id}>
                            {c.full_name || c.email.split("@")[0]}
                          </option>
                        ))}
                      </select>
                    ) : assignments[punchKey(p)] ? (
                      <span
                        title="Assigned contractor"
                        style={{ fontSize: 11, color: C.steel, whiteSpace: "nowrap" }}
                      >
                        ▸ {assignments[punchKey(p)].contractor_name || "contractor"}
                      </span>
                    ) : null}
                    <span className="punchstate" style={{ color: p.resolved ? C.grass : C.rust }}>
                      {p.resolved ? "RESOLVED" : "OPEN"}
                    </span>
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <div className="punch-empty">
            <div className="pe-ico">✓</div>
            <div className="pe-t">{isContractor ? "Nothing assigned to you" : "No rejected items"}</div>
            <div className="pe-s">
              {isContractor
                ? "No punch items have been assigned to you yet. A QC manager assigns NCRs to contractors for close-out — assigned items will appear here."
                : "Nothing has been failed yet. Items you reject in Quality Control — at table, module or pile level — will collect here, grouped by category."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
